import { ReactNode, useMemo } from 'react'
import type { SurveyFilterOption } from 'app/common/types/surveys'
import PlainModal, {
  PlainModalHeader,
  PlainModalContent,
  HandleModalDisplayed,
} from 'app/common/ui-next/plain-modal'
import PlainInputSearch from 'app/common/ui-next/plain-input-search'
import ScrollbarsWShadow from 'app/common/ui-components/scrollbars-w-shadow'
import EmptyWarn from 'app/common/layouts/EmptyWarn'
import useSearch from 'app/common/hooks/useSearch'
import { sortByTitle } from 'app/common/lib/sort'
import styles from './SurveyValuesModal.module.scss'

type Props = {
  header: string
  items: Array<SurveyFilterOption>
  districts?: Array<SurveyFilterOption>
  children: (toggle: HandleModalDisplayed) => ReactNode
}

export const SurveyValuesModal = ({
  header,
  items,
  districts = [],
  children,
}: Props) => {
  const [search, handleSearch, clearSearch] = useSearch()
  const grouped = districts.length > 0 || items.some((item) => item.districtId != null)
  const groups = useMemo(() => {
    const normalize = (value: string) => value.toLowerCase().replace(/\s|"|'|»|«|“|”/g, '')
    const query = normalize(search)
    const result = new Map<string, Array<SurveyFilterOption>>()
    items.forEach((item) => {
      if (!normalize(item.title).includes(query)) return
      const key = grouped ? item.districtId || '0' : ''
      result.set(key, [...(result.get(key) || []), item])
    })
    return sortByTitle(
      Array.from(result, ([id, entries]) => ({
        id,
        title: grouped
          ? districts.find((district) => district.id === id)?.title ||
            (id === '0' ? 'Без района' : `Район ${id}`)
          : '',
        items: sortByTitle(entries),
      })),
    )
  }, [items, districts, grouped, search])

  return (
    <PlainModal
      className={styles.modal}
      controlBy={children}
      onDisplayStatusChange={(visible) => {
        if (visible) clearSearch()
      }}
    >
      <PlainModalHeader>{header}</PlainModalHeader>
      <PlainModalContent unscrollable>
        <PlainInputSearch
          className={styles.search}
          value={search}
          onChange={handleSearch}
          clearSearch={clearSearch}
          placeholder="Поиск"
          ContainerComponent="div"
        />
      </PlainModalContent>
      <PlainModalContent paddingFree unscrollable>
        <ScrollbarsWShadow autoHeight autoHeightMin={100} autoHeightMax={210}>
          {!groups.length && <EmptyWarn>{'Нет записей, удовлетворяющих условиям'}</EmptyWarn>}
          {groups.map((group) => (
            <div key={group.id}>
              {group.title && <div className={styles.group}>{group.title}</div>}
              {group.items.map((item) => (
                <div key={item.id} className={styles.item}>
                  {item.title}
                </div>
              ))}
            </div>
          ))}
        </ScrollbarsWShadow>
      </PlainModalContent>
    </PlainModal>
  )
}
